import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralMessages = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");
  const [messages, setMessages] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    subject: "",
    message: "",
    type: "Team",
    recipient: "",
    date: "",
    status: "Open",
  };

  const [formData, setFormData] = useState(initialForm);

  // ---------------- SAMPLE DATA ----------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
    { id: 3, name: "Customer Portal" },
  ];

  const demoMessages = [
    {
      id: 1,
      projectId: 1,
      subject: "Client UI Feedback",
      message: "Client requested color changes in homepage banner.",
      type: "Client",
      recipient: "Design Team",
      date: "2026-03-14",
      status: "Open",
    },
    {
      id: 2,
      projectId: 1,
      subject: "API Delay",
      message: "Backend API integration delayed by 2 days.",
      type: "Team",
      recipient: "Project Manager",
      date: "2026-03-12",
      status: "Closed",
    },
    {
      id: 3,
      projectId: 2,
      subject: "Vendor License Update",
      message: "ERP vendor sent updated license agreement.",
      type: "Vendor",
      recipient: "Finance",
      date: "2026-03-11",
      status: "Open",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setMessages(demoMessages);
  };

  // ---------------- CRUD ----------------

  const handleAdd = () => {

    if (!selectedProjectId) {
      alert("Please select a project first.");
      return;
    }

    setEditingMessage(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (msg) => {

    setEditingMessage(msg);

    setFormData({
      subject: msg.subject,
      message: msg.message,
      type: msg.type,
      recipient: msg.recipient,
      date: msg.date,
      status: msg.status,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {

    if (!window.confirm("Delete this communication?")) return;

    setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSubmit = (e) => {

    e.preventDefault();

    if (!formData.subject || !formData.message || !formData.date) {
      alert("Subject, message and date are required.");
      return;
    }

    const newMessage = {
      id: editingMessage?.id || Date.now(),
      projectId: Number(selectedProjectId),
      ...formData,
    };

    if (editingMessage) {

      setMessages((prev) =>
        prev.map((m) => (m.id === editingMessage.id ? newMessage : m))
      );

    } else {

      setMessages((prev) => [...prev, newMessage]);

    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // ---------------- FILTER ----------------

  const projectMessages = messages.filter(
    (m) => String(m.projectId) === String(selectedProjectId)
  );

  const filtered = projectMessages.filter(
    (m) =>
      m.subject.toLowerCase().includes(search.toLowerCase()) ||
      m.type.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const typeColor = {
    Team: "bg-cyan-600",
    Client: "bg-green-600",
    Vendor: "bg-indigo-600",
    Stakeholder: "bg-yellow-600",
  };

  return (

    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">

      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">

        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            💬 SprintCentral Communications
          </div>
          <div className="text-xs text-gray-400">
            Agile Project Team & Stakeholder Messaging
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">

          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>

              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}

        </div>

      </header>

      <div className="mt-4 min-h-[75vh]">

        {/* ACTION BAR */}

        <div className="mt-4 flex flex-col lg:flex-row gap-4">

          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Messages
          </button>

          <button
            onClick={handleAdd}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Message
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>

              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">

            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              placeholder="Search communication..."
              value={search}
              style={{ width: "100%" }}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />

          </div>

        </div>

        {/* MODAL */}

        {showModal && (

          <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">

            <div className="flex justify-between items-center mb-4">

              <h2 className="text-lg text-cyan-200 font-semibold">
                {editingMessage ? "Edit Message" : "New Message"}
              </h2>

              <button
                className="w-12 bg-blue-500"
                onClick={() => setShowModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>

            </div>

            <form onSubmit={handleSubmit} className="space-y-3">

              <input
                type="text"
                placeholder="Subject"
                required
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <textarea
                placeholder="Message details"
                required
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2 h-24"
              />

              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="w-48 h-12 bg-gray-900 border border-gray-700 rounded p-2"
              >
                <option>Team</option>
                <option>Client</option>
                <option>Vendor</option>
                <option>Stakeholder</option>
              </select>

              <input
                type="text"
                placeholder="Recipient / Department"
                value={formData.recipient}
                onChange={(e) =>
                  setFormData({ ...formData, recipient: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <input
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                className="w-48 bg-white border border-gray-700 rounded p-2 text-black"
              />

              <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
              >
                {editingMessage ? "Update Message" : "Create Message"}
              </button>

            </form>

          </div>

        )}

        {/* MESSAGE CARDS */}

        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">

          {paginated.map((msg) => (

            <div
              key={msg.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >

              <div className="flex justify-between items-start">

                <h3 className="font-semibold text-white">
                  {msg.subject}
                </h3>

                <span
                  className={`px-2 py-1 rounded text-xs ${typeColor[msg.type]}`}
                >
                  {msg.type}
                </span>

              </div>

              <p className="text-xs text-gray-300 mt-2 line-clamp-3">
                {msg.message}
              </p>

              <div className="text-xs text-gray-400 mt-3">
                Recipient: {msg.recipient || "-"}
              </div>

              <div className="text-xs text-gray-400">
                Date: {msg.date}
              </div>

              <div className="mt-4 flex gap-2">

                <button
                  onClick={() => handleEdit(msg)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>

                <button
                  onClick={() => handleDelete(msg.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>

              </div>

            </div>

          ))}

        </div>

      </div>

      {/* FOOTER */}

      <footer className="bg-black border-t border-blue-400 text-white py-4">

        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">

          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>

          <div className="flex flex-wrap gap-4 text-sm">

            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>

          </div>

        </div>

      </footer>

    </div>
  );
};

export default SprintCentralMessages;